import EmptyCartImage from '@assets/icons/empty-cart-image.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AD_VIEWABILITY_CONFIG, useAdsContext } from '@/contexts/ads-context';
import { AdImpressionSource } from '@/contexts/ads-context-types';
import {
  CARD_VIEW_V2_GRID_CELL_LIST_ITEM_STYLE,
  CARD_VIEW_V2_GRID_LIST_ITEM_STYLE,
} from '@/utils/card-view-v2-layout';
import { CatalogItemListLoading } from './catalog-item-list-loading';
import { CategoryCatalogReturn } from '@/hooks/catalog-search/use-filtered-category-catalog';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { ItemListOption } from '@/contexts/item-context-types';
import { Props as ListItemProps } from '@/components/list-item';
import { MinimalOrderableItemListItemV2 } from '@/components/minimal-orderable-item-list-item-v2';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from '@/components/orders/orderable-item-list-item';
import { SCREEN_PADDING } from '@/styles';
import { ScrollViewProps, View } from 'react-native';
import { useCardViewV2ProductGridLayout } from '@/hooks/use-card-view-v2-product-grid-layout';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useMultiOrderGuideButton } from '@/hooks/use-multi-order-guide-button';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useShowListView } from '@/hooks/use-show-list-view';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';

export interface Props {
  categoryCatalog: CategoryCatalogReturn;
  scrollViewProps?: ScrollViewProps;
}

export const CatalogCategoryList: React.FC<Props> = ({
  categoryCatalog,
  scrollViewProps,
}) => {
  const [styles] = useStyles();
  const { showListView, showListViewToggle } = useShowListView({
    enableOnSmallLayouts: true,
  });
  const cardViewV2Enabled = useSupplierDynamicConfigValue(
    'card_view_v2_enabled',
    false,
  );
  const shouldUseCardV2Grid = cardViewV2Enabled && !showListView;
  const cardV2Grid = useCardViewV2ProductGridLayout(shouldUseCardV2Grid);
  const listRowItemLayout = showListView && showListViewToggle;

  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { selectItemListOption } = useItemDetailContext();
  const { onViewableAdItemsChanged } = useAdsContext();

  const {
    renderOrderGuideButton,
    shouldShowOrderGuideButton,
    renderPartitionSelectorSheet,
  } = useMultiOrderGuideButton();

  const { listData, loading, fetchNextPage, fetchMoreOffset } = categoryCatalog;

  const listItemProps: Partial<ListItemProps<ItemListOption>> = useMemo(() => {
    return {
      ...(shouldUseCardV2Grid
        ? { listItemStyle: CARD_VIEW_V2_GRID_CELL_LIST_ITEM_STYLE }
        : {}),
      onSelect: (i) => {
        selectItemListOption(i, { source: 'category_list' });
      },
      renderMainContent: (option) => {
        if (shouldUseCardV2Grid) {
          return (
            <MinimalOrderableItemListItemV2
              item={option}
              onSelect={() => {
                selectItemListOption(option, { source: 'category_list' });
              }}
              size="large"
              style={styles.cardV2ListItem}
              updateQuantity={(updateQuantityData) => {
                debouncedUpdateQuantity(updateQuantityData, {
                  source: 'catalog',
                });
              }}
            />
          );
        }

        const showOrderGuideButton = shouldShowOrderGuideButton(option);

        return (
          <OrderableItemListItemWithRowQuantity
            isFocused={false}
            onOptionsPress={(i) => {
              selectItemListOption(i, { source: 'category_list' });
            }}
            option={option}
            renderOrderGuideButton={
              showOrderGuideButton ? renderOrderGuideButton : undefined
            }
            showListView={listRowItemLayout}
            showSingleOption
            source="category_list"
            updateQuantity={(updateQuantityData) => {
              debouncedUpdateQuantity(updateQuantityData, {
                source: 'catalog',
              });
            }}
          />
        );
      },
    };
  }, [
    debouncedUpdateQuantity,
    renderOrderGuideButton,
    selectItemListOption,
    shouldShowOrderGuideButton,
    listRowItemLayout,
    shouldUseCardV2Grid,
    styles.cardV2ListItem,
  ]);

  // With local GTIN filters, it's possible that there are no items in list data
  // but we need the list to render to fetch the next pages from the server
  const renderEmptyState =
    !loading && listData.length === 0 && typeof fetchMoreOffset !== 'number';

  return (
    <>
      <View style={styles.content} testID="catalog-category-list">
        {loading ? (
          <View style={[styles.loadingContainer]}>
            <CatalogItemListLoading />
          </View>
        ) : (
          <>
            {renderEmptyState ? (
              <EmptyStateMinimal
                containerStyle={styles.emptyStateContainer}
                ImageComponent={EmptyCartImage}
                text={i18n.t('no_search_results')}
              />
            ) : (
              <OrderableItemList
                gridColumnCount={
                  shouldUseCardV2Grid ? cardV2Grid.columnCount : undefined
                }
                hasExternalSeparatorControl={shouldUseCardV2Grid}
                listContentContainerStyle={
                  shouldUseCardV2Grid
                    ? cardV2Grid.listContentContainerStyle
                    : undefined
                }
                listData={listData}
                listItemProps={listItemProps}
                listRowItemLayout={listRowItemLayout}
                onEndReached={fetchNextPage}
                onViewableItemsChanged={({ viewableItems }) =>
                  onViewableAdItemsChanged(
                    viewableItems,
                    AdImpressionSource.Catalog,
                  )
                }
                scrollViewProps={scrollViewProps}
                shouldRenderSeparator={() => !shouldUseCardV2Grid}
                showListView={showListView}
                viewabilityConfig={AD_VIEWABILITY_CONFIG}
              />
            )}
          </>
        )}
      </View>
      {renderPartitionSelectorSheet()}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  cardV2ListItem: {
    ...CARD_VIEW_V2_GRID_LIST_ITEM_STYLE,
  },
  content: {
    flex: 1,
  },
  emptyStateContainer: {
    backgroundColor: theme.colors.transparent,
  },
  loadingContainer: {
    flex: 1,
    marginTop: SCREEN_PADDING,
  },
}));
